import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save } from 'lucide-react';
import { createProduto, getProduto, Produto, updateProduto, proximoCodigoProduto } from '../api/produto';
import { apiError } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import FormField from '../components/FormField';

type FormValues = Partial<Produto>;

export default function ProdutoEditPage() {
  const { id } = useParams();
  const isNew = !id || id === 'novo';
  const pid = isNew ? null : Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const q = useQuery({
    enabled: !isNew && !!pid,
    queryKey: ['produto', pid],
    queryFn: () => getProduto(pid!)
  });

  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>({ defaultValues: { ucom: 'UN', tipo: 'produto' } });
  const codigoAtual = watch('codigo');

  useEffect(() => {
    if (q.data) reset(q.data);
  }, [q.data, reset]);

  // Em novo produto, sugere próximo código sequencial ao abrir tela
  useEffect(() => {
    if (!isNew) return;
    let cancel = false;
    proximoCodigoProduto()
      .then((codigo) => {
        if (cancel) return;
        // só preenche se o usuário ainda não digitou nada
        if (!codigoAtual || codigoAtual === '') setValue('codigo', codigo);
      })
      .catch(() => { /* silencioso — deixa o campo vazio para digitação manual */ });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  async function onSubmit(values: FormValues) {
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        ...values,
        valor_unitario: values.valor_unitario != null && values.valor_unitario !== '' ? Number(values.valor_unitario) : 0
      };
      const saved = isNew ? await createProduto(payload) : await updateProduto(pid!, payload);
      qc.invalidateQueries({ queryKey: ['produtos'] });
      navigate(`/produtos/${saved.id}`, { replace: true });
    } catch (e) {
      setErr(apiError(e, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  }

  if (!isNew && q.isLoading) {
    return <div className="py-10 grid place-items-center"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="flex items-center gap-2">
        <Link to="/produtos" className="btn-ghost h-11 w-11 !px-2 !py-1 md:h-9 md:w-9">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <div className="eyebrow">Cadastro fiscal</div>
          <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">
            {isNew ? 'Novo produto' : 'Editar produto'}
          </h1>
        </div>
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <section className="card-surface p-4 space-y-4 md:p-5">
          <div>
            <h2 className="eyebrow">Dados fiscais</h2>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              Campos usados na emissão de NF-e e nos rascunhos de documento.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Código" {...register('codigo')} hint={isNew ? 'Sugerido automaticamente (editável)' : undefined} />
            <FormField label="Descrição" className="md:col-span-2" {...register('descricao')} />
            <FormField label="EAN" {...register('ean')} />
            <FormField label="NCM" {...register('ncm')} />
            <FormField label="CFOP padr?o" {...register('cfop_padrao')} />
            <FormField label="UCom (Unidade)" {...register('ucom')} />
            <FormField label="Valor unit?rio (R$)" type="number" step="0.0001" {...register('valor_unitario')} />
            <FormField label="CSOSN" {...register('csosn')} />
            <FormField label="CST ICMS" {...register('cst_icms')} />
            <FormField label="Origem" {...register('orig')} hint="0-8" />
            <FormField label="CEST" {...register('cest')} />
            <div>
              <label className="field-label">Tipo</label>
              <select className="field-input" {...register('tipo')}>
                <option value="produto">Produto</option>
                <option value="serviço">Serviço</option>
              </select>
            </div>
          </div>
        </section>

        {err && (
          <div className="text-sm text-danger bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md px-3 py-2">
            {err}
          </div>
        )}

        <div className="grid gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm dark:border-white/10 dark:bg-slate-950/70 dark:shadow-none sm:flex sm:justify-end">
          <Link to="/produtos" className="btn-secondary justify-center">Cancelar</Link>
          <button type="submit" disabled={saving} className="btn-primary justify-center">
            <Save className="h-4 w-4" />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
